import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SearchField } from "@/components/app/patterns/search-field";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { sortRows } from "@/components/app/lists/table-sort";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { Printer } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, optionalString, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { ProviderPicker } from "./-components/provider-picker";
import { RecordVisitDialog } from "./-components/record-visit-dialog";
import { ReportGroup, sortValue, type ReportRow, type SortKey } from "./-components/report-group";

const SORT_KEYS = [
  "room",
  "dob",
  "gender",
  "pharmacy",
  "admission",
  "pcp",
  "problems",
  "lastVisit",
  "interval",
  "status",
] as const satisfies readonly SortKey[];
const STATUSES = ["current", "overdue", "never"] as const;

type PcpSearch = {
  q?: string;
  status?: (typeof STATUSES)[number];
  sort?: (typeof SORT_KEYS)[number];
  desc?: true;
  pq?: string;
  ptype?: string;
};

export const Route = createFileRoute("/_authenticated/_care/pcp-visits/")({
  validateSearch: (search: Record<string, unknown>): PcpSearch => ({
    q: optionalSearch(search.q),
    status: optionalOneOf(STATUSES, search.status),
    sort: optionalOneOf(SORT_KEYS, search.sort),
    desc: search.desc === true || search.desc === "true" ? true : undefined,
    pq: optionalString(search.pq)?.trim().slice(0, 120) || undefined,
    ptype: optionalString(search.ptype)?.slice(0, 60) || undefined,
  }),
  component: PcpVisitsPage,
});

/** Who on a doctor's list has not been seen, and for how long. The question is asked one physician
 * at a time, because that is how the building works: somebody is about to call Dr Byron's office
 * and wants the list of her residents to raise while they have them on the phone. */
function PcpVisitsPage() {
  const { facilityId } = useFacility();
  const [selected, setSelected] = useState<string[]>([]);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<PcpSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const { text: providerSearch, change: setProviderSearch } = useSearchParamText(params.pq, (pq) =>
    patch({ pq }),
  );
  const { text: residentSearch, change: setResidentSearch } = useSearchParamText(params.q, (q) =>
    patch({ q }),
  );
  const providerType = params.ptype ?? "all";
  const setProviderType = (next: string) => patch({ ptype: next === "all" ? undefined : next });
  const status = params.status ?? "all";
  const setStatus = (next: string) => patch({ status: optionalOneOf(STATUSES, next) });
  const sortKey: SortKey = params.sort ?? "residentName";
  const sortDir = params.desc ? "desc" : "asc";
  const [recording, setRecording] = useState<ReportRow | null>(null);

  const providers = useQuery(
    orpc.care.directories.providers.list.queryOptions({
      input: { facilityId, includeInactive: false },
    }),
  );
  const report = useQuery({
    ...orpc.care.pcpVisits.report.queryOptions({ input: { providerIds: selected, facilityId } }),
    enabled: selected.length > 0,
  });

  const term = providerSearch.trim().toLowerCase();
  const pickable = (providers.data ?? []).filter((provider) => {
    if (providerType !== "all" && provider.providerType !== providerType) return false;
    if (!term) return true;
    return [provider.firstName, provider.lastName, provider.specialty, provider.credentials]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term));
  });

  const rows = report.data ?? [];
  const stats = {
    total: rows.length,
    current: rows.filter((row) => row.status === "current").length,
    overdue: rows.filter((row) => row.status === "overdue").length,
    never: rows.filter((row) => row.status === "never").length,
  };
  const needle = residentSearch.trim().toLowerCase();
  const shown = rows.filter(
    (row) =>
      (status === "all" || row.status === status) &&
      (!needle ||
        row.residentName.toLowerCase().includes(needle) ||
        (row.roomNumber ?? "").toLowerCase().includes(needle)),
  );
  const groups = [...new Map(shown.map((row) => [row.providerId, row])).values()].map((first) => ({
    provider: {
      id: first.providerId,
      name: first.providerName,
      credentials: first.providerCredentials,
      type: first.providerType,
      specialty: first.providerSpecialty,
    },
    rows: sortRows(
      shown.filter((row) => row.providerId === first.providerId),
      sortDir,
      (row) => sortValue(row, sortKey),
    ),
  }));

  const onSort = (key: SortKey) => {
    if (sortKey === key) patch({ desc: params.desc ? undefined : true });
    else patch({ sort: optionalOneOf(SORT_KEYS, key), desc: undefined });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="PCP visit report"
        meta={
          selected.length === 0
            ? "Choose the physicians whose lists you want to work through."
            : `${stats.overdue} overdue and ${stats.never} never visited, out of ${stats.total} residents`
        }
        actions={
          selected.length > 0 ? (
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="size-4" />
              Print
            </Button>
          ) : null
        }
      />

      <ProviderPicker
        providers={pickable}
        directory={providers.data ?? []}
        loading={providers.isPending}
        selected={selected}
        search={providerSearch}
        providerType={providerType}
        onSearch={setProviderSearch}
        onProviderType={setProviderType}
        onToggle={(id) =>
          setSelected((current) =>
            current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
          )
        }
        onSelectAll={() => {
          const ids = pickable.map((provider) => provider.id);
          const allSelected = ids.every((id) => selected.includes(id));
          setSelected((current) =>
            allSelected
              ? current.filter((id) => !ids.includes(id))
              : [...new Set([...current, ...ids])],
          );
        }}
        onClear={() => setSelected([])}
      />

      {selected.length === 0 ? (
        <Card className="py-0">
          <EmptyState
            title="Pick a physician"
            description="The report is built one doctor's list at a time."
          />
        </Card>
      ) : report.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : report.isError ? (
        <ReadFailed
          message="Could not load the visit report."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : rows.length === 0 ? (
        <Card className="py-0">
          <EmptyState
            title="Nobody on those lists"
            description="No active resident is linked to the physicians you chose."
          />
        </Card>
      ) : (
        <>
          <StatTiles className="xl:grid-cols-4">
            <StatTile label="Residents" value={stats.total} hint="On the chosen lists" />
            <StatTile label="Current" value={stats.current} hint={`of ${stats.total}`} />
            <StatTile label="Overdue" value={stats.overdue} hint={`of ${stats.total}`} />
            <StatTile label="Never visited" value={stats.never} hint={`of ${stats.total}`} />
          </StatTiles>

          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <SearchField
              value={residentSearch}
              onChange={setResidentSearch}
              placeholder="Search resident or room"
              label="Search residents"
            />
            <OptionSelect
              className="w-44"
              aria-label="Visit status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <SelectOption value="all">All statuses</SelectOption>
              <SelectOption value="current">Current</SelectOption>
              <SelectOption value="overdue">Overdue</SelectOption>
              <SelectOption value="never">Never visited</SelectOption>
            </OptionSelect>
            <span className="text-muted-foreground text-sm">
              {shown.length} of {stats.total} shown
            </span>
          </div>

          {groups.length === 0 ? (
            <Card className="py-0">
              <EmptyState
                title="Nothing matches"
                description="Widen the search or change the status filter."
              />
            </Card>
          ) : (
            groups.map((group) => (
              <ReportGroup
                key={group.provider.id}
                provider={group.provider}
                rows={group.rows}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
                onRecordVisit={setRecording}
              />
            ))
          )}
        </>
      )}

      {recording ? (
        <RecordVisitDialog
          residentId={recording.residentId}
          residentName={recording.residentName}
          facilityId={recording.facilityId}
          providerId={recording.providerId}
          providerName={recording.providerName}
          onClose={() => setRecording(null)}
        />
      ) : null}
    </div>
  );
}
